import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Badge, type BadgeVariant } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import { Textarea } from '@heyroot/ui/primitives/Textarea';
import { ChevronLeft } from 'lucide-react';
import {
  fetchEscalationDetail,
  fetchChatMessages,
  sendChatMessage,
  resolveEscalation,
  queryKeys,
  type ChatMessageRow,
} from '../lib/api';

interface Props {
  escalationId: string;
}

function tierVariant(tier: string | null): BadgeVariant {
  if (tier === 'high') return 'success';
  if (tier === 'medium') return 'warning';
  if (tier === 'low') return 'danger';
  return 'neutral';
}

export function EscalationChatPage({ escalationId }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [messageBody, setMessageBody] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [showResolveForm, setShowResolveForm] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: escalation, isPending: escPending } = useQuery({
    queryKey: queryKeys.escalation(escalationId),
    queryFn: () => fetchEscalationDetail(escalationId),
  });

  const { data: messages = [], isPending: msgsPending } = useQuery({
    queryKey: queryKeys.chatMessages(escalationId),
    queryFn: () => fetchChatMessages(escalationId),
    refetchInterval: 15_000,
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const sendMutation = useMutation({
    mutationFn: () => sendChatMessage(escalationId, messageBody.trim()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.chatMessages(escalationId) });
      setMessageBody('');
    },
  });

  const resolveMutation = useMutation({
    mutationFn: () => resolveEscalation(escalationId, resolutionNotes.trim() || null),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.escalation(escalationId) });
      setShowResolveForm(false);
    },
  });

  if (escPending || msgsPending) {
    return <div className="text-body-sm text-muted animate-pulse">Loading…</div>;
  }

  const isClosed = escalation?.status === 'closed';

  return (
    <div className="flex flex-col gap-6 max-w-3xl">
      {/* Back nav */}
      {/* eslint-disable-next-line @heyroot/heyroot-ui/no-one-off-ui -- plain back-nav button */}
      <button
        type="button"
        onClick={() => void navigate({ to: '/assistant/$escalationId', params: { escalationId } })}
        className="inline-flex items-center gap-1 text-body-sm text-muted hover:text-primary transition-colors bg-transparent border-none cursor-pointer p-0 self-start"
      >
        <ChevronLeft size={14} />
        Back to escalation
      </button>

      {/* Header */}
      {escalation && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-heading-2 text-primary">Live Chat</h1>
            <Badge variant={isClosed ? 'neutral' : 'warning'}>{escalation.status}</Badge>
            {escalation.confidenceTier && (
              <Badge variant={tierVariant(escalation.confidenceTier)}>
                {escalation.confidenceTier} confidence
              </Badge>
            )}
          </div>
          <p className="text-caption text-muted">
            Escalation {escalationId.slice(0, 8)}… · tenant {escalation.tenantId.slice(0, 8)}…
          </p>
          {isClosed && escalation.resolutionNotes && (
            <div className="mt-2 rounded-control border border-border bg-surface-sunken p-3">
              <p className="text-caption text-muted mb-0.5">Resolution notes</p>
              <p className="text-body-sm text-secondary">{escalation.resolutionNotes}</p>
            </div>
          )}
        </div>
      )}

      {/* Context summary */}
      {escalation && (
        <div className="rounded-control border border-border bg-surface p-4 flex flex-col gap-2">
          <p className="text-body-sm-emphasis leading-4 text-muted">Original question</p>
          <p className="text-body-sm text-primary">{escalation.question}</p>
          {escalation.assistantResponse && (
            <>
              <p className="text-body-sm-emphasis leading-4 text-muted mt-1">Assistant response</p>
              <p className="text-body-sm text-secondary whitespace-pre-wrap">
                {escalation.assistantResponse}
              </p>
            </>
          )}
        </div>
      )}

      {/* Chat thread */}
      <div className="flex flex-col gap-1">
        <h2 className="text-heading-3 text-primary">Thread</h2>
        <div className="flex flex-col gap-2 max-h-96 overflow-y-auto rounded-control border border-border p-3 bg-surface-sunken">
          {messages.length === 0 && (
            <p className="text-body-sm text-muted text-center py-4">No messages yet.</p>
          )}
          {messages.map((msg: ChatMessageRow) => {
            const isStaff = msg.authorType === 'staff';
            return (
              <div
                key={msg.id}
                className={`flex flex-col gap-0.5 ${isStaff ? 'items-end' : 'items-start'}`}
              >
                <div
                  className={`rounded-control px-3 py-2 text-body-sm max-w-[80%] ${
                    isStaff
                      ? 'bg-selected text-selected-fg'
                      : 'bg-surface border border-border text-secondary'
                  }`}
                >
                  {msg.body}
                </div>
                <p className="text-caption text-muted">
                  {isStaff ? 'Staff' : 'System'} · {new Date(msg.createdAt).toLocaleTimeString()}
                </p>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Message input */}
      {!isClosed && (
        <div className="flex flex-col gap-2">
          <Textarea
            value={messageBody}
            onChange={(e) => setMessageBody(e.target.value)}
            rows={3}
            maxLength={10000}
            placeholder="Type a message…"
            disabled={sendMutation.isPending}
          />
          <div className="flex justify-between items-center gap-2">
            <Button
              onClick={() => sendMutation.mutate()}
              disabled={!messageBody.trim() || sendMutation.isPending}
            >
              {sendMutation.isPending ? 'Sending…' : 'Send message'}
            </Button>
            <Button variant="secondary" onClick={() => setShowResolveForm((v) => !v)}>
              {showResolveForm ? 'Cancel resolve' : 'Resolve escalation'}
            </Button>
          </div>
          {sendMutation.isError && (
            <p role="alert" className="text-body-sm text-danger-text">
              Failed to send. Please try again.
            </p>
          )}
        </div>
      )}

      {/* Resolve form */}
      {showResolveForm && !isClosed && (
        <div className="rounded-control border border-border bg-surface p-4 flex flex-col gap-3">
          <h2 className="text-heading-3 text-primary">Resolve escalation</h2>
          <div className="flex flex-col gap-1">
            <label htmlFor="resolution-notes" className="text-caption text-muted">
              Resolution notes (optional)
            </label>
            <Textarea
              id="resolution-notes"
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              rows={3}
              maxLength={10000}
              placeholder="Describe how this was resolved…"
            />
          </div>
          {resolveMutation.isError && (
            <p role="alert" className="text-body-sm text-danger-text">
              Failed to resolve. Please try again.
            </p>
          )}
          <div>
            <Button
              variant="primary"
              onClick={() => resolveMutation.mutate()}
              disabled={resolveMutation.isPending}
            >
              {resolveMutation.isPending ? 'Resolving…' : 'Confirm resolve'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
